import React, { useEffect, useReducer, useRef, useState } from "react";
import TrackPickerModal from "./TrackPickerModal";
import { decodeAudioBuffer, detectAudioSupport, getAudioContextClass } from "./audioUtils";
import {
  buildStemState,
  CONTROLS_GLOBAL_BOTTOM_OFFSET,
  DEFAULT_SONG_ID,
  getSongById,
  MASTER_FADER_IMAGE_URL,
  MASTER_FADER_LAYOUT,
  PLAY_BUTTONS_OFF,
  PLAY_BUTTONS_ON,
  SONGS,
} from "./songConfig";

const initialState = {
  displaySongId: DEFAULT_SONG_ID,
  isPlaying: false,
  isTrackPickerOpen: true,
  loadedSongId: null,
  loadPercent: 0,
  loadingSongId: null,
  masterVolume: 1,
  mutes: buildStemState(false),
  solos: buildStemState(false),
  volumes: buildStemState(1),
};

function stemPlayerReducer(state, action) {
  switch (action.type) {
    case "openTrackPicker":
      return { ...state, isTrackPickerOpen: true };
    case "closeTrackPicker":
      if (!state.loadedSongId || state.loadingSongId) return state;
      return { ...state, isTrackPickerOpen: false };
    case "startSongLoad":
      return {
        ...state,
        displaySongId: action.songId,
        isPlaying: false,
        isTrackPickerOpen: true,
        loadPercent: 0,
        loadingSongId: action.songId,
      };
    case "updateLoadPercent":
      return { ...state, loadPercent: action.percent };
    case "finishSongLoad":
      return {
        ...state,
        displaySongId: action.songId,
        isTrackPickerOpen: false,
        loadedSongId: action.songId,
        loadPercent: 100,
        loadingSongId: null,
      };
    case "play":
      return { ...state, isPlaying: true };
    case "pause":
      return { ...state, isPlaying: false };
    case "setStemVolume":
      return {
        ...state,
        volumes: {
          ...state.volumes,
          [action.stemId]: action.volume,
        },
      };
    case "setMasterVolume":
      return { ...state, masterVolume: action.volume };
    case "toggleMute":
      return {
        ...state,
        mutes: {
          ...state.mutes,
          [action.stemId]: !state.mutes[action.stemId],
        },
      };
    case "toggleSolo":
      return {
        ...state,
        solos: {
          ...state.solos,
          [action.stemId]: !state.solos[action.stemId],
        },
      };
    default:
      return state;
  }
}

function getStemGain(stemId, volume, masterVolume, solos, mutes) {
  const anySolo = Object.values(solos).some(Boolean);
  let gain = volume * masterVolume;

  if (anySolo && !solos[stemId]) {
    gain = 0;
  }

  if (mutes[stemId]) {
    gain = 0;
  }

  return gain;
}

export default function StemPlayer() {
  const [state, dispatch] = useReducer(stemPlayerReducer, initialState);
  const [hasAudioContext, setHasAudioContext] = useState(false);

  const loadedAssetsRef = useRef(0);
  const formatSupportRef = useRef(null);
  const audioContextRef = useRef(null);
  const buffersRef = useRef({});
  const sourcesRef = useRef({});
  const offsetRef = useRef(0);
  const playStartTimeRef = useRef(null);

  const activeSong = getSongById(state.loadedSongId || DEFAULT_SONG_ID);
  const displaySong = getSongById(state.displaySongId);
  const songBeingLoaded = state.loadingSongId
    ? getSongById(state.loadingSongId)
    : null;
  const hasLoadedSong = state.loadedSongId != null;
  const isSongLoading = songBeingLoaded != null;
  const isReady = hasLoadedSong && !isSongLoading;
  const stems = activeSong.stems;

  const stopAllSources = () => {
    Object.values(sourcesRef.current).forEach(entry => {
      if (!entry || !entry.source) return;
      try {
        entry.source.stop();
      } catch {}
    });

    sourcesRef.current = {};
  };

  useEffect(() => {
    if (audioContextRef.current) return;

    const AudioContextClass = getAudioContextClass();
    if (!AudioContextClass) {
      console.warn("Web Audio API not supported.");
      return;
    }

    try {
      audioContextRef.current = new AudioContextClass();
      setHasAudioContext(true);
    } catch (error) {
      console.warn("Failed to create AudioContext:", error);
    }
  }, []);

  useEffect(() => {
    formatSupportRef.current = detectAudioSupport();
  }, []);

  useEffect(() => {
    if (!songBeingLoaded || !hasAudioContext) return;

    const context = audioContextRef.current;
    const support = formatSupportRef.current || {
      canOgg: false,
      canAac: true,
    };

    if (!context) {
      console.warn("No AudioContext yet; stems will not load.");
      return;
    }

    let cancelled = false;
    const stemsToLoad = songBeingLoaded.stems;
    const totalAssets = stemsToLoad.length + 2;

    stopAllSources();
    buffersRef.current = {};
    offsetRef.current = 0;
    playStartTimeRef.current = null;
    loadedAssetsRef.current = 0;

    const markAssetLoaded = () => {
      if (cancelled) return;

      loadedAssetsRef.current += 1;
      const percent = Math.round(
        (loadedAssetsRef.current / totalAssets) * 100
      );

      dispatch({ type: "updateLoadPercent", percent });

      if (loadedAssetsRef.current >= totalAssets) {
        dispatch({
          type: "finishSongLoad",
          songId: songBeingLoaded.id,
        });
      }
    };

    [songBeingLoaded.staticBgUrl, songBeingLoaded.gifUrl].forEach(url => {
      const image = new Image();
      image.onload = markAssetLoaded;
      image.onerror = markAssetLoaded;
      image.src = url;
    });

    async function loadSongStems() {
      for (const stem of stemsToLoad) {
        if (cancelled) return;

        let sourceUrl = stem.aacUrl;
        if (support.canOgg && stem.oggUrl) {
          sourceUrl = stem.oggUrl;
        } else if (support.canAac && stem.aacUrl) {
          sourceUrl = stem.aacUrl;
        }

        if (!sourceUrl) {
          markAssetLoaded();
          continue;
        }

        try {
          const response = await fetch(sourceUrl);
          const arrayBuffer = await response.arrayBuffer();
          const buffer = await decodeAudioBuffer(context, arrayBuffer);
          if (cancelled) return;

          buffersRef.current[stem.id] = buffer;
          markAssetLoaded();
        } catch (error) {
          console.error(`Failed to load/decode stem "${stem.id}"`, error);
          markAssetLoaded();
        }
      }
    }

    loadSongStems();

    return () => {
      cancelled = true;
      stopAllSources();
    };
  }, [hasAudioContext, songBeingLoaded]);

  useEffect(() => {
    Object.entries(sourcesRef.current).forEach(([stemId, entry]) => {
      if (!entry || !entry.gainNode) return;

      const gain = getStemGain(
        stemId,
        state.volumes[stemId] ?? 1,
        state.masterVolume,
        state.solos,
        state.mutes
      );

      entry.gainNode.gain.value = gain;
    });
  }, [state.masterVolume, state.mutes, state.solos, state.volumes]);

  const handlePlay = async () => {
    if (state.isPlaying || !isReady) return;

    const context = audioContextRef.current;
    if (!context) {
      console.warn("No AudioContext available for playback.");
      return;
    }

    if (context.state === "suspended") {
      try {
        await context.resume();
      } catch (error) {
        console.warn("Error resuming AudioContext:", error);
      }
    }

    stopAllSources();

    const startTime = context.currentTime + 0.05;
    const offset = offsetRef.current;
    playStartTimeRef.current = startTime;

    stems.forEach(stem => {
      const buffer = buffersRef.current[stem.id];
      if (!buffer) return;

      const source = context.createBufferSource();
      source.buffer = buffer;
      source.loop = true;

      const gainNode = context.createGain();
      gainNode.gain.value = getStemGain(
        stem.id,
        state.volumes[stem.id] ?? 1,
        state.masterVolume,
        state.solos,
        state.mutes
      );

      source.connect(gainNode).connect(context.destination);

      const duration = buffer.duration || 0;
      const startOffset =
        duration > 0 ? (offset % duration + duration) % duration : 0;

      try {
        source.start(startTime, startOffset);
      } catch (error) {
        console.warn(`Error starting source for "${stem.id}"`, error);
      }

      sourcesRef.current[stem.id] = { gainNode, source };
    });

    dispatch({ type: "play" });
  };

  const handlePause = () => {
    if (!state.isPlaying) return;

    const context = audioContextRef.current;
    if (context && playStartTimeRef.current != null) {
      const delta = context.currentTime - playStartTimeRef.current;
      if (delta > 0) {
        offsetRef.current += delta;
      }
    }

    stopAllSources();
    dispatch({ type: "pause" });
  };

  const handleSongSelect = songId => {
    if (isSongLoading) return;

    if (songId === state.loadedSongId) {
      dispatch({ type: "closeTrackPicker" });
      return;
    }

    stopAllSources();
    dispatch({ type: "startSongLoad", songId });
  };

  return (
    <div className="stem-player-shell">
      <div className="stem-player-container">
        <div className="stem-player-bg">
          <img
            src={displaySong.staticBgUrl}
            alt=""
            className={`stem-bg-image ${state.isPlaying ? "" : "is-visible"}`}
          />
          <img
            src={displaySong.gifUrl}
            alt=""
            className={`stem-bg-image ${state.isPlaying ? "is-visible" : ""}`}
          />
        </div>

        <div className="stem-player-overlay">
          <div className="stem-visual-buttons">
            {state.isPlaying ? (
              <img src={PLAY_BUTTONS_ON} alt="" />
            ) : (
              <img src={PLAY_BUTTONS_OFF} alt="" />
            )}
          </div>

          <button
            className={
              "stem-player-play-button" + (state.isPlaying ? " is-playing" : "")
            }
            onClick={handlePlay}
          />

          <button
            className={
              "stem-player-stop-button" + (state.isPlaying ? " is-playing" : "")
            }
            onClick={handlePause}
          />

          <div className="stem-faders-wrapper">
            {stems.map(stem => (
              <div
                key={`${stem.id}-controls`}
                className="stem-fader-controls"
                style={{
                  bottom: `${
                    stem.controlsBottomPercent + CONTROLS_GLOBAL_BOTTOM_OFFSET
                  }%`,
                  left: `${stem.controlsLeftPercent}%`,
                }}
              >
                <button
                  type="button"
                  className={
                    "stem-fader-btn stem-fader-btn--solo" +
                    (state.solos[stem.id] ? " is-active" : "")
                  }
                  aria-label={`${stem.name} solo ${state.solos[stem.id] ? "on" : "off"}`}
                  aria-pressed={state.solos[stem.id]}
                  onClick={() => dispatch({ type: "toggleSolo", stemId: stem.id })}
                />
                <button
                  type="button"
                  className={
                    "stem-fader-btn stem-fader-btn--mute" +
                    (state.mutes[stem.id] ? " is-active" : "")
                  }
                  aria-label={`${stem.name} mute ${state.mutes[stem.id] ? "on" : "off"}`}
                  aria-pressed={state.mutes[stem.id]}
                  onClick={() => dispatch({ type: "toggleMute", stemId: stem.id })}
                />
              </div>
            ))}

            {stems.map(stem => (
              <div
                key={stem.id}
                className="stem-fader"
                style={{
                  bottom: `${stem.bottomPercent}%`,
                  left: `${stem.leftPercent}%`,
                }}
              >
                <input
                  className="stem-fader-slider"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  style={{
                    "--stem-thumb-image": `url("${stem.imageUrl}")`,
                  }}
                  value={state.volumes[stem.id]}
                  onChange={event =>
                    dispatch({
                      type: "setStemVolume",
                      stemId: stem.id,
                      volume: Number(event.target.value),
                    })
                  }
                />
              </div>
            ))}

            <div
              className="stem-master-fader"
              style={{
                bottom: `${MASTER_FADER_LAYOUT.bottomPercent}%`,
                left: `${MASTER_FADER_LAYOUT.leftPercent}%`,
              }}
            >
              <input
                className="stem-fader-slider stem-fader-slider--master"
                type="range"
                min="0"
                max="1"
                step="0.01"
                style={{
                  "--stem-thumb-image": `url("${MASTER_FADER_IMAGE_URL}")`,
                }}
                value={state.masterVolume}
                onChange={event =>
                  dispatch({
                    type: "setMasterVolume",
                    volume: Number(event.target.value),
                  })
                }
              />
            </div>
          </div>
        </div>

        <TrackPickerModal
          hasLoadedSong={hasLoadedSong}
          isLoading={isSongLoading}
          isOpen={state.isTrackPickerOpen}
          loadPercent={state.loadPercent}
          loadingSongTitle={songBeingLoaded?.title ?? "track"}
          onClose={() => dispatch({ type: "closeTrackPicker" })}
          onSelect={handleSongSelect}
          selectedSongId={state.loadedSongId}
          songs={SONGS}
        />
      </div>

      {hasLoadedSong && !state.isTrackPickerOpen ? (
        <div className="stem-player-track-cta">
          <button
            type="button"
            className="stem-player-track-button"
            onClick={() => dispatch({ type: "openTrackPicker" })}
          >
            Switch Track
          </button>
        </div>
      ) : null}
    </div>
  );
}
